import React, { useMemo, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, ArrowRight, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getCompanyFundIds = (company) => {
  if (!company) return [];
  if (Array.isArray(company.fund_ids) && company.fund_ids.length) return company.fund_ids;
  if (company.fund_id) return [company.fund_id];
  return [];
};

const PortfolioCard = ({ company, index, onClick, fundNameById }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="relative cursor-pointer"
      data-testid={`featured-portfolio-${company.company_id || company.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        aria-label={`View ${company.name} portfolio details`}
        className={`
          group relative w-full bg-white rounded-3xl p-7
          transition-all duration-300 ease-out cursor-pointer
          border h-full flex flex-col text-left
          ${isHovered 
            ? 'shadow-[0_24px_48px_-12px_rgba(168,87,81,0.15)] -translate-y-2 border-[#A85751]/20' 
            : 'shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08)] border-gray-100'
          }
        `}
        onClick={() => onClick(company)}
      >
        {/* Logo and Title Section - Enhanced Focus */}
        <div className="flex flex-col items-center text-center mb-6 pb-6 border-b border-gray-100">
          {/* Logo with enhanced styling */}
          <div 
            className={`
              relative w-[7.2rem] h-[7.2rem] rounded-2xl flex items-center justify-center mb-4 p-3
              transition-all duration-300 bg-white
              ${isHovered 
                ? 'shadow-[0_8px_24px_-6px_rgba(168,87,81,0.3)] scale-105' 
                : 'shadow-[0_4px_16px_-4px_rgba(0,0,0,0.1)]'
              }
            `}
          >
            <img
              src={company.logo_url?.startsWith('/') ? `${BACKEND_URL}${company.logo_url}` : company.logo_url || company.logo || `https://ui-avatars.com/api/?name=${company.name}&background=A85751&color=fff`}
              alt={company.logo_alt || company.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${company.name}&background=A85751&color=fff`;
              }}
            />
            {(company.status === 'exited' || company.isExit) && (
              <span className="absolute -top-1 -right-1 bg-[#A85751] text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                EXITED
              </span>
            )}
          </div>

          {/* Title with enhanced styling */}
          <h3 
            className={`
              text-2xl md:text-3xl font-semibold transition-colors duration-300
              ${isHovered ? 'text-[#A85751]' : 'text-gray-900'}
            `}
          >
            {company.name}
          </h3>
        </div>

        {/* Content Section - Better aligned */}
        <div className="flex flex-col flex-grow">
          {/* Tags */}
          <div className="flex gap-2 mb-4 flex-wrap justify-center">
            {(company.sectors || [company.sector]).filter(Boolean).slice(0, 2).map((sector, idx) => (
              <span 
                key={idx}
                className={`
                  px-3 py-1.5 text-xs rounded-full font-medium transition-all duration-300
                  ${isHovered 
                    ? 'bg-[#A85751] text-white' 
                    : 'bg-red-50 text-[#A85751]'
                  }
                `}
              >
                {sector}
              </span>
            ))}
          </div>

          {/* Funds */}
          {getCompanyFundIds(company).length > 0 && (
            <div className="flex gap-2 mb-4 flex-wrap justify-center">
              {getCompanyFundIds(company).slice(0, 2).map((fid) => (
                <span
                  key={fid}
                  className="px-3 py-1.5 text-xs rounded-full font-medium bg-gray-100 text-gray-700"
                >
                  {fundNameById?.get(fid) || fid}
                </span>
              ))}
              {getCompanyFundIds(company).length > 2 && (
                <span className="text-xs text-muted-light">+{getCompanyFundIds(company).length - 2}</span>
              )}
            </div>
          )}

          {/* Description */}
          <p className="text-gray-600 text-center leading-relaxed text-sm">
            {company.short_description || company.description || 'No description available'}
          </p>
        </div>

        {/* View Details Link - Centered and at bottom */}
        <div 
          className={`
            transition-all duration-300 mt-6 pt-6 border-t border-gray-100
            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
          `}
        >
          <span className="flex items-center justify-center gap-2 text-[#A85751] font-medium w-full" aria-hidden="true">
            <span>View Details</span>
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </span>
        </div>
      </button>
    </motion.div>
  );
};

const CompanyModal = ({ company, isOpen, onClose, fundNameById }) => {
  if (!company) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div className="w-32 h-32 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border">
              <img
                src={company.logo_url?.startsWith('/') ? `${BACKEND_URL}${company.logo_url}` : company.logo_url || company.logo || `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`}
                alt={company.logo_alt || company.name}
                className="w-full h-full object-contain p-1.5"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`;
                }}
              />
            </div>
            <div className="flex-1 flex flex-col justify-center h-32">
              <DialogTitle className="text-4xl font-bold text-[#211F1F] mb-3 leading-tight">
                {company.name}
              </DialogTitle>
              <div className="flex flex-wrap gap-2">
                {(company.sectors || [company.sector]).filter(Boolean).map((sector, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-[#C8473A]/10 text-yn-brand-on-tint text-sm">
                    {sector}
                  </Badge>
                ))}
                {(company.status === 'exited' || company.isExit) && (
                  <Badge className="bg-[#C8473A] text-white text-sm">EXITED</Badge>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">About</h4>
            <p className="text-gray-600 leading-relaxed">
              {company.long_description || company.short_description || company.description || 'No description available'}
            </p>
          </div>

          {/* Funds */}
          {getCompanyFundIds(company).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Funds</h4>
              <div className="flex flex-wrap gap-2">
                {getCompanyFundIds(company).map((fid) => (
                  <Badge key={fid} variant="secondary" className="bg-[#C8473A]/10 text-yn-brand-on-tint text-sm">
                    {fundNameById?.get(fid) || fid}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {company.founders?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Founders</h4>
              <div className="flex flex-wrap gap-2">
                {company.founders.map((founder, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-gray-100 rounded-full text-sm font-medium">
                    {typeof founder === 'string' ? founder : founder.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Fund Team Members */}
          {company.team_members_details?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">YourNest Team</h4>
              <div className="flex flex-wrap gap-3">
                {company.team_members_details.map((member, idx) => (
                  <Link 
                    key={idx} 
                    to={`/team/${member.slug || member.member_id}`}
                    onClick={() => onClose()}
                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
                  >
                    {member.photo_url ? (
                      <img 
                        src={member.photo_url.startsWith('/') ? `${BACKEND_URL}${member.photo_url}` : member.photo_url}
                        alt={member.photo_alt || member.name}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${member.name}&background=C8473A&color=fff&size=32`;
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#C8473A]/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-yn-brand-on-tint">{member.name?.charAt(0)}</span>
                      </div>
                    )}
                    <span className="text-sm font-medium hover:text-[#C8473A] transition-colors">{member.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {(company.website_url || company.website) && (
            <div className="pt-4 border-t">
              <a
                href={company.website_url || company.website}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${company.name} website (opens in new tab)`}
                className="inline-flex items-center gap-2 text-[#C8473A] font-medium hover:underline"
              >
                Visit Website <ExternalLink className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PortfolioSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [companies, setCompanies] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const fundNameById = useMemo(() => {
    const map = new Map();
    (funds || []).forEach((f) => map.set(f.fund_id, f.name));
    return map;
  }, [funds]);

  useEffect(() => {
    fetchFeaturedCompanies();
  }, []);

  const fetchFeaturedCompanies = async () => {
    try {
      const [featuredRes, filtersRes] = await Promise.all([
        axios.get(`${API}/portfolio/featured?limit=4`),
        axios.get(`${API}/portfolio/filters`)
      ]);
      setCompanies(featuredRes.data);
      setFunds(filtersRes.data?.funds || []);
    } catch (error) {
      console.error('Failed to fetch featured companies:', error);
      // Fallback to empty array - no mock data
      setCompanies([]);
      setFunds([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="portfolio" className="py-24 bg-[#FAFAFA] relative" aria-label="Portfolio highlights">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-[#C8473A] font-medium uppercase tracking-wider text-sm">Portfolio</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#211F1F] mt-4 mb-6">
            <span className="text-[#C8473A]">Portfolio</span> Highlights
          </h2>
          <p className="text-xl text-gray-600">
            We specialise in patentable DeepTech investments, supporting cutting-edge companies that leverage advanced technologies including Industrial AI, ML, robotics, and quantum computing to solve complex problems, and create transformative solutions across industries.
          </p>
        </motion.div>

        {/* Portfolio Grid - Featured Companies Only */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
                <div className="w-16 h-16 bg-gray-200 rounded-xl mb-4"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-100 rounded w-1/2 mb-3"></div>
                <div className="h-12 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        ) : companies.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Portfolio companies coming soon</p>
          </motion.div>
        ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
                className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            data-testid="featured-portfolio-grid"
          >
            {companies.map((company, index) => (
              <PortfolioCard 
                key={company.company_id || company.id} 
                company={company} 
                index={index}
                onClick={setSelectedCompany}
                fundNameById={fundNameById}
              />
                ))}
              </motion.div>
        )}

        {/* View All Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-[#C8473A] font-medium hover:gap-3 transition-all"
            data-testid="view-all-portfolio-link"
          >
            View Complete Portfolio <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>

      {/* Company Detail Modal */}
      <CompanyModal
        company={selectedCompany}
        isOpen={!!selectedCompany}
        onClose={() => setSelectedCompany(null)}
        fundNameById={fundNameById}
      />
    </section>
  );
};

export default PortfolioSection;
